// parley: our playout stage for remote audio (docs/voice.md, "Eigene
// Abspielstufe").
//
// @moq/watch decodes audio on the main thread and hands the samples to an
// AudioWorklet ("render") that plays them out of a ring buffer. That ring
// has no answer to late data: an underrun cuts to zero mid-waveform, what
// arrives after it plays on at a higher latency until the ring overflows,
// and an overflow jumps ahead - the cuts and crackles we hear. This worklet
// replaces it (installPlayoutWorklet, patches/@moq__watch@0.5.4.patch) and
// speaks the same message protocol, but plays out like a real-time jitter
// buffer (in the spirit of WebRTC's NetEQ):
//
// - Concealment instead of cuts: when data is missing, the last pitch
//   period is repeated and faded out; when data comes back it is faded in
//   (crossfaded with what concealment was still playing).
// - Nothing is thrown away: the read position waits during a gap, so late
//   data is still played, just later.
// - An adaptive target: every underrun raises it by 20 ms (up to 400 ms),
//   three seconds without one lower it by 10 ms (never below the latency
//   the library asks for). A pause in speech is not an underrun.
// - Catching up without jumps: excess delay is dropped inside silence,
//   where nobody hears it; only when far behind it is shortened in speech,
//   with a short crossfade.
//
// The source is a string, not a function, so no build step can rewrite it
// (helpers, minified names) - it runs in the AudioWorkletGlobalScope, which
// sees nothing of the page. playoutWorklet.test.ts runs it in Node.

export const PLAYOUT_WORKLET_SOURCE = `
const MS = (rate, ms) => Math.round((rate * ms) / 1000);

class ParleyPlayout extends AudioWorkletProcessor {
	constructor() {
		super();
		this.ready = false;
		this.quanta = 0;
		this.port.onmessage = (event) => this.onMessage(event.data);
	}

	onMessage(msg) {
		if (!msg || typeof msg.type !== 'string') return;
		if (msg.type === 'init-post') return this.init(msg);
		if (msg.type === 'init-shared') {
			// The page is not cross-origin isolated, so the library never takes
			// this path; if it ever does, say so instead of playing silence.
			console.warn('[parley playout] the shared ring buffer is not supported');
			return;
		}
		if (!this.ready) return;
		if (msg.type === 'data') this.write(msg.timestamp, msg.data);
		else if (msg.type === 'latency') this.setMinTarget(msg.latency);
		else if (msg.type === 'truncate') this.truncate(msg.timestamp);
		else if (msg.type === 'reset') this.reset();
	}

	init(msg) {
		const rate = msg.rate;
		this.rate = rate;
		this.channels = msg.channels;
		this.capacity = Math.ceil(rate * 2);
		this.buf = [];
		for (let c = 0; c < this.channels; c++) this.buf.push(new Float32Array(this.capacity));
		// have[i]: sample i of the ring holds received audio not yet played.
		this.have = new Uint8Array(this.capacity);
		this.maxTarget = MS(rate, 400);
		this.step = MS(rate, 20);
		this.fade = MS(rate, 30);
		this.fadeInLen = MS(rate, 5);
		this.xfade = MS(rate, 2);
		this.joinLen = MS(rate, 1);
		// What was actually played, the model for concealment: the ring may
		// hold stale samples where a gap was.
		this.histLen = MS(rate, 15) * 2 + MS(rate, 10) + 2;
		this.hist = [];
		this.snap = [];
		for (let c = 0; c < this.channels; c++) {
			this.hist.push(new Float32Array(this.histLen));
			this.snap.push(new Float32Array(this.histLen));
		}
		this.histPos = 0;
		this.minPeriod = MS(rate, 2.5);
		this.maxPeriod = MS(rate, 15);
		this.cleanQuanta = Math.round((rate * 3) / 128);
		this.pauseGap = MS(rate, 60);
		this.raiseGap = Math.round((rate * 0.2) / 128);
		this.minTarget = Math.max(MS(rate, 20), MS(rate, msg.latency || 100));
		this.target = this.minTarget;
		this.stats = { underruns: 0, expands: 0, concealed: 0, dropped: 0, late: 0 };
		this.statsQuanta = 0;
		this.reset();
		this.ready = true;
	}

	reset() {
		// A reset (new codec epoch, re-anchoring) drops what is buffered;
		// whatever was sounding fades out like a gap instead of stopping dead.
		const wasSounding = this.anchored && this.hist !== undefined;
		this.readIndex = 0;
		this.writeIndex = 0;
		this.ahead = 0; // received samples in [readIndex, writeIndex)
		this.anchored = false;
		this.playing = false;
		this.inHole = false;
		this.concealLeft = 0;
		this.concealStep = 0;
		this.period = this.minPeriod || 1;
		this.fadeInLeft = 0;
		this.sinceUnderrun = 0;
		this.sinceRaise = 1e9;
		if (this.have) this.have.fill(0);
		if (wasSounding) this.startConcealment();
	}

	setMinTarget(latencyMs) {
		this.minTarget = Math.max(MS(this.rate, 20), MS(this.rate, latencyMs));
		if (this.target < this.minTarget) this.target = this.minTarget;
	}

	slot(index) {
		const n = this.capacity;
		return ((index % n) + n) % n;
	}

	at(c, index) {
		return this.buf[c][this.slot(index)];
	}

	// Samples are placed by their timestamp, so a frame lands where it
	// belongs even when frames arrive late or out of order; what is missing
	// is decided when it is due (process), not here.
	write(timestamp, data) {
		if (!data || data.length === 0) return;
		const frames = data[0].length;
		const start = Math.round((timestamp / 1e6) * this.rate);
		if (!this.anchored) {
			this.readIndex = start;
			this.writeIndex = start;
			this.anchored = true;
		}
		const end = start + frames;
		if (end <= this.readIndex) {
			this.stats.late++;
			this.raise();
			return; // its time has passed (it was concealed)
		}
		if (end - this.readIndex > this.capacity) {
			if (!this.playing) {
				// A long pause: nothing waiting is worth keeping.
				this.have.fill(0);
				this.ahead = 0;
				this.readIndex = start;
				this.writeIndex = start;
			} else {
				this.skip(end - this.capacity - this.readIndex);
			}
		}
		const channels = Math.min(this.channels, data.length);
		for (let i = Math.max(0, this.readIndex - start); i < frames; i++) {
			const s = this.slot(start + i);
			for (let c = 0; c < this.channels; c++) this.buf[c][s] = data[Math.min(c, channels - 1)][i];
			if (!this.have[s]) {
				this.have[s] = 1;
				this.ahead++;
			}
		}
		if (end > this.writeIndex) this.writeIndex = end;
	}

	// Move the read position forward by n samples without playing them.
	skip(n) {
		for (let i = 0; i < n; i++) {
			const s = this.slot(this.readIndex);
			if (this.have[s]) {
				this.have[s] = 0;
				this.ahead--;
			}
			this.readIndex++;
		}
		this.stats.dropped += n;
	}

	// One step up of the target, at most every 200 ms.
	raise() {
		this.sinceUnderrun = 0;
		if (this.sinceRaise < this.raiseGap) return;
		this.sinceRaise = 0;
		this.target = Math.min(this.maxTarget, this.target + this.step);
	}

	// Every new gap starts its own concealment from the output just heard:
	// the last pitch period (normalized autocorrelation on channel 0) is
	// repeated, so the voice goes on instead of an arbitrary slice of it.
	startConcealment() {
		const n = this.histLen;
		for (let c = 0; c < this.channels; c++) {
			for (let i = 0; i < n; i++) this.snap[c][i] = this.hist[c][(this.histPos + i) % n];
		}
		const x = this.snap[0];
		const window = MS(this.rate, 10);
		let best = this.minPeriod;
		let bestScore = -Infinity;
		for (let lag = this.minPeriod; lag <= this.maxPeriod; lag++) {
			let dot = 0;
			let energy = 1e-9;
			for (let i = 1; i <= window; i++) {
				const a = x[n - i];
				const b = x[n - i - lag];
				dot += a * b;
				energy += b * b;
			}
			const score = dot / Math.sqrt(energy);
			if (score > bestScore) {
				bestScore = score;
				best = lag;
			}
		}
		this.period = best;
		this.concealStep = 0;
		this.concealLeft = this.fade;
		// The repeated period rarely starts exactly where the voice left off;
		// the difference to the predicted next sample decays over 1 ms, so the
		// join has no edge.
		this.joinOffset = [];
		for (let c = 0; c < this.channels; c++) {
			const y = this.snap[c];
			this.joinOffset.push(2 * y[n - 1] - y[n - 2] - y[n - best]);
		}
	}

	// The next concealment sample of channel c, under a raised-cosine fade
	// to silence.
	concealValue(c) {
		if (this.concealLeft <= 0) return 0;
		const n = this.histLen;
		const g = 0.5 * (1 + Math.cos((Math.PI * (this.fade - this.concealLeft)) / this.fade));
		let v = this.snap[c][n - this.period + (this.concealStep % this.period)];
		if (this.concealStep < this.joinLen) v += this.joinOffset[c] * (1 - this.concealStep / this.joinLen);
		return g * v;
	}

	// Remember what was played (see startConcealment).
	record(output, i) {
		for (let c = 0; c < this.channels; c++) this.hist[c][this.histPos] = output[Math.min(c, output.length - 1)][i];
		this.histPos = (this.histPos + 1) % this.histLen;
	}

	advanceConcealment() {
		if (this.concealLeft <= 0) return;
		this.concealLeft--;
		this.concealStep++;
		this.stats.concealed++;
	}

	truncate(timestamp) {
		const target = Math.round((timestamp / 1e6) * this.rate);
		if (target >= this.writeIndex) return;
		for (let i = Math.max(target, this.readIndex); i < this.writeIndex; i++) {
			const s = this.slot(i);
			if (this.have[s]) {
				this.have[s] = 0;
				this.ahead--;
			}
		}
		this.writeIndex = Math.max(target, this.readIndex);
	}

	// Excess delay goes where it is inaudible: inside silence it is skipped
	// outright; only far behind is it cut out of speech, with a crossfade.
	catchUp() {
		const excess = this.ahead - this.target;
		if (excess <= MS(this.rate, 20)) return;
		// Skip only as far as it stays silent, never into the next word.
		const limit = Math.min(excess - MS(this.rate, 10), this.writeIndex - this.readIndex - 128);
		let quiet = 0;
		while (quiet < limit) {
			let loud = false;
			for (let c = 0; c < this.channels && !loud; c++) loud = Math.abs(this.at(c, this.readIndex + quiet)) >= 0.003;
			if (loud) break;
			quiet++;
		}
		if (quiet >= 128) {
			this.skip(quiet - 64); // keep a little silence before the sound
			return;
		}
		if (excess > MS(this.rate, 150)) {
			// Cut one period-agnostic slice and crossfade across the seam. The
			// crossfade is no longer than the cut, and reads the samples it
			// fades from before it writes any: the two ranges overlap otherwise.
			const cut = 128;
			const x = Math.min(this.xfade, cut);
			for (let c = 0; c < this.channels; c++) {
				const from = new Float32Array(x);
				for (let i = 0; i < x; i++) from[i] = this.at(c, this.readIndex + i);
				for (let i = 0; i < x; i++) {
					const w = (i + 1) / (x + 1);
					const s = this.slot(this.readIndex + cut + i);
					this.buf[c][s] = from[i] * (1 - w) + this.buf[c][s] * w;
				}
			}
			this.skip(cut);
		}
	}

	process(_inputs, outputs) {
		const output = outputs[0];
		if (!output || output.length === 0 || !this.ready) return true;
		const frames = output[0].length;
		const chans = output.length;
		this.quanta++;
		this.sinceRaise++;
		if (++this.sinceUnderrun > this.cleanQuanta) {
			this.sinceUnderrun = 0;
			this.target = Math.max(this.minTarget, this.target - MS(this.rate, 10));
		}

		if (!this.playing && this.anchored && this.ahead >= this.target) {
			// Resume at the first sample that is really there, faded in over
			// whatever concealment is still sounding.
			const from = this.readIndex;
			while (this.readIndex < this.writeIndex && !this.have[this.slot(this.readIndex)]) this.readIndex++;
			// A long stretch nobody sent is a pause in speech (Opus DTX), not
			// a late network: the underrun that began it must not grow the
			// buffer.
			if (this.readIndex - from >= this.pauseGap && this.targetBeforeUnderrun !== undefined) {
				this.target = this.targetBeforeUnderrun;
			}
			this.targetBeforeUnderrun = undefined;
			this.playing = true;
			this.inHole = false;
			this.fadeInLeft = this.fadeInLen;
		}
		if (this.playing) this.catchUp();

		for (let i = 0; i < frames; i++) {
			if (this.playing) {
				const s = this.slot(this.readIndex);
				if (this.have[s]) {
					if (this.inHole) {
						// Merge: back from concealment into received audio.
						this.inHole = false;
						this.fadeInLeft = this.fadeInLen;
					}
					let w = 1;
					if (this.fadeInLeft > 0) {
						w = 1 - this.fadeInLeft / (this.fadeInLen + 1);
						this.fadeInLeft--;
					}
					for (let c = 0; c < chans; c++) {
						const ch = Math.min(c, this.channels - 1);
						output[c][i] = this.buf[ch][s] * w + (w < 1 ? this.concealValue(ch) * (1 - w) : 0);
					}
					this.advanceConcealment();
					this.have[s] = 0;
					this.ahead--;
					this.readIndex++;
					this.record(output, i);
					continue;
				}
				if (this.ahead > 0) {
					// Expand: a frame is missing but later ones are here; its
					// time is now, so conceal it and play on after it.
					if (!this.inHole) {
						this.inHole = true;
						this.stats.expands++;
						this.raise();
						this.startConcealment();
					}
					for (let c = 0; c < chans; c++) output[c][i] = this.concealValue(Math.min(c, this.channels - 1));
					this.advanceConcealment();
					this.readIndex++;
					this.record(output, i);
					continue;
				}
				// Underrun: nothing left to play. Conceal, then wait until the
				// buffer holds the (raised) target again.
				this.playing = false;
				this.inHole = false;
				this.stats.underruns++;
				this.targetBeforeUnderrun = this.target;
				this.raise();
				this.startConcealment();
			}
			for (let c = 0; c < chans; c++) output[c][i] = this.concealValue(Math.min(c, this.channels - 1));
			this.advanceConcealment();
			this.record(output, i);
		}

		if (this.quanta % 5 === 0) {
			this.port.postMessage({ type: 'state', timestamp: (this.readIndex / this.rate) * 1e6, stalled: !this.playing });
		}
		if (++this.statsQuanta * 128 >= this.rate) {
			this.statsQuanta = 0;
			this.port.postMessage({
				type: 'parley-playout',
				underruns: this.stats.underruns,
				expands: this.stats.expands,
				concealedMs: Math.round((this.stats.concealed * 1000) / this.rate),
				droppedMs: Math.round((this.stats.dropped * 1000) / this.rate),
				late: this.stats.late,
				targetMs: Math.round((this.target * 1000) / this.rate),
				fillMs: Math.round((this.ahead * 1000) / this.rate),
			});
		}
		return true;
	}
}

registerProcessor('render', ParleyPlayout);
`;

declare global {
	// Read by the patched @moq/watch (patches/@moq__watch@0.5.4.patch).
	var __parleyAddModule: ((context: BaseAudioContext, libraryWorklet: string) => Promise<void>) | undefined;
	var __parleyPlaybackContext: ((sampleRate: number) => AudioContext | undefined) | undefined;
}

/** Marks the AudioContext all remote audio is mixed in; @moq/watch never closes it. */
interface SharedContext extends AudioContext {
	__parleyShared?: boolean;
}

let playbackContext: SharedContext | null = null;
const loadedWorklets = new WeakMap<BaseAudioContext, Promise<void>>();

/**
 * All remote audio plays in one AudioContext (the app's shared voice context,
 * as upstream Fluxer mixes it with LiveKit's webAudioMix): no AudioContext
 * per participant, no hop through a MediaStream into an <audio> element and
 * a second audio clock. @moq/watch builds a decoder's graph in it whenever
 * the decoded sample rate matches, and never closes it.
 */
export function setPlaybackContext(context: AudioContext | null): void {
	playbackContext = context;
	if (context) (context as SharedContext).__parleyShared = true;
}

export function isSharedPlaybackContext(context: BaseAudioContext): boolean {
	return (context as SharedContext).__parleyShared === true;
}

/**
 * Makes @moq/watch load our playout worklet instead of its own, once per
 * AudioContext (a shared context must not register "render" twice), and
 * build its graphs in the shared playback context. The worklet only
 * implements the postMessage ring: a cross-origin-isolated page would use
 * the library's shared-memory ring, so there the library's worklet stays.
 */
export function installPlayoutWorklet(): void {
	if (typeof window === 'undefined' || globalThis.__parleyAddModule) return;
	const ours = (globalThis as {crossOriginIsolated?: boolean}).crossOriginIsolated
		? undefined
		: URL.createObjectURL(new Blob([PLAYOUT_WORKLET_SOURCE], {type: 'application/javascript'}));
	globalThis.__parleyAddModule = (context, libraryWorklet) => {
		let loaded = loadedWorklets.get(context);
		if (!loaded) {
			loaded = context.audioWorklet.addModule(ours ?? libraryWorklet);
			loadedWorklets.set(context, loaded);
		}
		return loaded;
	};
	globalThis.__parleyPlaybackContext = (sampleRate) =>
		playbackContext && playbackContext.state !== 'closed' && playbackContext.sampleRate === sampleRate
			? playbackContext
			: undefined;
}
